/**
 * The site's sectors: one list a raise is filed under, whatever its platform
 * calls it. A platform tags raises in its own words and language ("Fintech &
 * Finance", "Financial services & payments", "Salud digital"), so the raw tags
 * stay on `campaign.sectors`, as the source states them, and what they come to
 * is kept beside them (`campaign.normalised_sectors`; ./mapping.ts).
 *
 * A sector's slug is its address, /raises/sector/{slug}: renaming one moves a
 * public page, and its old slug then has to answer 301.
 */
export const SECTORS = [
  { slug: 'fintech', name: 'Fintech', inSentence: 'fintech' },
  { slug: 'healthtech', name: 'Healthtech', inSentence: 'healthtech' },
  { slug: 'biotech', name: 'Biotech', inSentence: 'biotech' },
  {
    slug: 'food-and-drink',
    name: 'Food and drink',
    inSentence: 'food and drink',
  },
  {
    slug: 'alcohol-and-beverages',
    name: 'Alcohol and beverages',
    inSentence: 'alcohol and beverages',
  },
  {
    slug: 'consumer-products',
    name: 'Consumer products',
    inSentence: 'consumer products',
  },
  { slug: 'saas', name: 'SaaS', inSentence: 'SaaS' },
  { slug: 'ai', name: 'AI', inSentence: 'AI' },
  {
    slug: 'energy-and-cleantech',
    name: 'Energy and cleantech',
    inSentence: 'energy and cleantech',
  },
  { slug: 'real-estate', name: 'Real estate', inSentence: 'real estate' },
  { slug: 'proptech', name: 'Proptech', inSentence: 'proptech' },
  { slug: 'mobility', name: 'Mobility', inSentence: 'mobility' },
  {
    slug: 'media-and-entertainment',
    name: 'Media and entertainment',
    inSentence: 'media and entertainment',
  },
  { slug: 'sports', name: 'Sports', inSentence: 'sports' },
  { slug: 'hospitality', name: 'Hospitality', inSentence: 'hospitality' },
  { slug: 'education', name: 'Education', inSentence: 'education' },
  { slug: 'gaming', name: 'Gaming', inSentence: 'gaming' },
  { slug: 'robotics', name: 'Robotics', inSentence: 'robotics' },
  { slug: 'agtech', name: 'Agtech', inSentence: 'agtech' },
  { slug: 'other', name: 'Other', inSentence: 'other' },
] as const;

export type SectorSlug = (typeof SECTORS)[number]['slug'];

/** Where a raise goes whose tags name none of the others. */
export const OTHER_SECTOR_SLUG = 'other';

/** Every sector a raise can be named for, "other" left out. */
export type NamedSectorSlug = Exclude<SectorSlug, typeof OTHER_SECTOR_SLUG>;

export type Sector = (typeof SECTORS)[number];

const BY_SLUG: ReadonlyMap<string, Sector> = new Map(
  SECTORS.map((sector) => [sector.slug, sector]),
);

/** A sector by its slug; undefined for a word that is not one. */
export function sectorBySlug(slug: string): Sector | undefined {
  return BY_SLUG.get(slug);
}

/**
 * The sector a raise's page links, of those it is filed under: the first, its
 * first tag's, unless that is "other", which no page links.
 */
export function linkedSector(normalised: readonly string[]): Sector | null {
  const first = normalised[0];
  if (first === undefined || first === OTHER_SECTOR_SLUG) return null;
  return sectorBySlug(first) ?? null;
}
